import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import { AppState } from '@app/store';
import {
  updateCatalogueByRoute,
  openCategory,
  setPopular,
} from '@app/store/catalogue/thunks';
import {
  setHeaderHeight,
  setWindowHeight,
  setWindowWidth,
  resetCategoriesHeight,
} from '@app/store/sizes/actions';
import { sendRouteEvent } from '@app/store/stats/thunks';
import { setFromRoute } from '@app/store/catalogue/actions';

import CategoryList from '@app/components/CategoryList/CategoryList';
import ServiceList from '@app/components/ServiceList/ServiceList';
import CatalogTitle from '@app/components/CatalogTitle/CatalogTitle';
import TizerList from '@app/components/TizerList/TizerList';
import SearchService from '@app/components/SearchService/SearchService';
import { Category } from '@app/models/Category/Category.interface';
import { sendRouteChange } from '@app/services/api.service';
import { isIEDetect } from '@app/helpers/utils';
import { setMeta } from '@app/services/meta.services';
import { eventBus } from '@app/services/eventBus';
import { Ref, RefObject } from 'react';
import { AppProps } from './AppProps';
import { fetchIcons } from './store/icons/thunks';
import { FROM_SUPERAPP } from './constants/fromParams';
import { TAB_PARAMS } from './constants/tabParams';
const { detect } = require('detect-browser');

declare global {
  interface Window {
    $mos_layouts: { on: Function };
    catalog: any;
  }
}

class App extends React.Component<AppProps, any> {
  private searchRef: Ref<HTMLDivElement>;
  private mainRef: RefObject<HTMLDivElement>;
  private isSuperApp = false;

  constructor(props: AppProps) {
    super(props);
    this.searchRef = React.createRef<HTMLDivElement>();
    this.mainRef = React.createRef<HTMLDivElement>();

    const urlParams = new URLSearchParams(this.props.location?.search);
    if (urlParams.get('from') === FROM_SUPERAPP) {
      sessionStorage.setItem('from', FROM_SUPERAPP);
    }
    if (sessionStorage.getItem('from') === FROM_SUPERAPP) {
      this.props.setFromRoute(FROM_SUPERAPP);
      this.isSuperApp = true;
      if (urlParams.get('tab') !== TAB_PARAMS.POPULAR) {
        this.props.setPopular(false);
      }
    }
    if (urlParams.get('tab') === TAB_PARAMS.CATEGORIES) {
      this.props.setPopular(false);
    }
    if (urlParams.get('disableHeader') === 'true') {
      sessionStorage.setItem('disableHeader', 'true');
    }
  }

  componentDidMount() {
    if (window.$mos_layouts && window.$mos_layouts.on) {
      this.apiReady();
    } else {
      document.addEventListener('change:displayHeader', this.apiReady, false);
    }
    if (window.catalog) {
      this.props.updateCatalogueByRoute(null, window.catalog);
    } else {
      this.props.updateCatalogueByRoute(this.props.location.pathname);
    }
    this.props.fetchIcons();
    this.updateWindowDimensions();
    window.addEventListener('resize', this.updateWindowDimensions);
    document.getElementsByTagName('html')[0].classList.add(detect().name);
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions);
  }

  catalogueTypeChangeHandler = (route: string) => {
    this.props.updateCatalogueByRoute(route);
    setTimeout(() => {
      sendRouteChange({
        url: this.props.stats!.currentRouteUrl,
        title: this.props.stats!.currentRouteTitle,
        referer: this.props.stats!.prevRouteUrl,
      });
    });
  };

  componentDidUpdate(prevProps: AppProps) {
    // Проверяем изменения роутинга, чтобы обновить страницу при
    // переходе по истории браузера
    if (this.props.location.pathname !== prevProps.location.pathname) {
      if (!this.props.catalogue!.isBlocked) {
        this.props.updateCatalogueByRoute(
          this.props.location.pathname,
          null,
          true
        );
      }
    }

    if (this.props.maxHeight !== prevProps.maxHeight) {
      this.updateWindowDimensions();
    }

    if (this.props.catalogueType !== prevProps.catalogueType) {
      this.props.resetCategoriesHeight();
    }

    // После загрузки новой категории
    if (this.props.lastOpenedCategory !== prevProps.lastOpenedCategory) {
      // При переходе между типами каталога
      if (this.props.catalogue!.levels.length === 1) {
        eventBus.dispatch('catalogTypeUpdated');
      }

      if (setMeta) {
        const levelCategories: Category[] = [];
        for (let i = 0; i < this.props.catalogue!.levels.length; i++) {
          let category =
            this.props.catalogue!.categories[this.props.catalogue!.levels[i]];
          if (category) levelCategories.push(category);
        }
        setMeta({
          catalogueType: this.props.catalogue!.type,
          levelCategories,
          routePathName: `${window.location.origin}${this.props.location.pathname}`,
        });
      }
    }
  }

  updateWindowDimensions = () => {
    const { headerHeight, setWindowHeight, setWindowWidth, maxHeight } =
      this.props;
    let contentHeight: 'auto' | number =
      window.innerWidth > 1023
        ? window.innerHeight - headerHeight! - 10 //высота окна минус высота шапки. 10 - отступ снизу
        : 'auto';
    if (contentHeight !== 'auto' && contentHeight > maxHeight) {
      contentHeight = maxHeight;
    }
    setWindowHeight(isIEDetect() ? 'auto' : contentHeight);
    setWindowWidth(window.innerWidth);
  };

  apiReady = () => {
    const { setHeaderHeight } = this.props;
    if (window.$mos_layouts && window.$mos_layouts.on) {
      window.$mos_layouts.on('change:headerHeight', (height: number) => {
        if (height && isFinite(height)) {
          // 10 - отступ сверху
          setHeaderHeight(window.innerWidth > 1023 ? height + 10 : 0);
          this.updateWindowDimensions();
        }
      });
    }
  };

  render() {
    const { windowHeight, windowWidth } = this.props;
    return (
      <div className="catalogue-app" role="main" ref={this.mainRef}>
        {!this.isSuperApp && (
          <CatalogTitle handleClick={this.catalogueTypeChangeHandler} />
        )}
        <div className="catalogue-app-wrap" style={{ minHeight: windowHeight }}>
          {windowWidth! <= 1023 && <SearchService ref={this.searchRef} />}
          <CategoryList />
          <ServiceList />
          <TizerList />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  catalogue: state.catalogue,
  stats: state.stats,
  lastOpenedCategory: state.catalogue.lastOpenedCategory,
  isPopular: state.catalogue.isPopular,
  headerHeight: state.sizes.headerHeight,
  windowHeight: state.sizes.windowHeight,
  windowWidth: state.sizes.windowWidth,
  maxHeight: state.sizes.maxHeight,
  catalogueType: state.catalogue.type,
});

const dispatchProps = {
  updateCatalogueByRoute,
  fetchIcons,
  openCategory,
  setHeaderHeight,
  setWindowHeight,
  setWindowWidth,
  sendRouteEvent,
  setPopular,
  resetCategoriesHeight,
  setFromRoute,
};

export default withRouter<any, any>(
  // @ts-ignore
  connect(mapStateToProps, dispatchProps, null, { forwardRef: true })(App)
);
